import ImageUploadField from '../../components/ImageUploadField';
import { useRef } from 'react';
import { updateBookPricing } from '../../utils/bookPricing';
import { FiBookOpen, FiCalendar, FiFileText, FiGlobe, FiImage, FiPackage, FiTag } from 'react-icons/fi';

const cardColors = {
  blue: 'border-blue-200 [&>h3]:bg-blue-50 [&>h3]:text-blue-700',
  green: 'border-emerald-200 [&>h3]:bg-emerald-50 [&>h3]:text-emerald-700',
  violet: 'border-violet-200 [&>h3]:bg-violet-50 [&>h3]:text-violet-700',
  cyan: 'border-cyan-200 [&>h3]:bg-cyan-50 [&>h3]:text-cyan-800',
  rose: 'border-rose-200 [&>h3]:bg-rose-50 [&>h3]:text-rose-700',
};

const SectionCard = ({ title, icon: Icon, color = 'blue', children }) => (
  <section className={`min-w-0 overflow-hidden rounded-md border bg-white ${cardColors[color]}`}>
    <h3 className="flex items-center gap-2 px-3 py-2 text-xs font-semibold">
      <Icon size={15} aria-hidden="true" />{title}
    </h3>
    <div className="p-3">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <label className="block min-w-0">
    <span className="text-[11px] font-semibold text-slate-700 mb-1 block">{label}</span>
    {children}
  </label>
);

const inputCls =
  'w-full min-w-0 border border-blue-200 rounded-md bg-white px-2 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200';

export const BasicInfoSection = ({ form, setForm, categories }) => (
  <SectionCard title="Basic Information" icon={FiFileText}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="SKU">
        <input type="text" maxLength={100} value={form.sku || ''} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={inputCls} placeholder="e.g. BOOK-001" />
      </Field>
      <Field label="Title *">
        <input
          type="text"
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Subtitle">
        <input
          type="text"
          value={form.subtitle}
          onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Author *">
        <input
          type="text"
          required
          value={form.author}
          onChange={(e) => setForm({ ...form, author: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Category">
        <select
          value={form.categoryId}
          onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          className={inputCls}
        >
          <option value="">Select Category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
    </div>
  </SectionCard>
);

export const PricingSection = ({ form, setForm }) => {
  const pricingBasis = useRef({ INR: 'price', USD: 'price' });
  const changePricing = (currency, field, value) => {
    if (field === 'discount' || field === 'usdDiscount') {
      pricingBasis.current[currency] = value !== '' && Number(value) >= 0 && Number(value) <= 100
        ? 'discount' : 'price';
    } else if (field === 'price' || field === 'usdPrice' || value === '') {
      pricingBasis.current[currency] = 'price';
    }
    setForm(updateBookPricing(form, currency, field, value, pricingBasis.current[currency]));
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <SectionCard title="Pricing & Stock (India)" icon={FiTag}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Selling Price (₹) *">
        <input
          type="number"
          step="0.01"
          min="0"
          required
          value={form.price}
          onChange={(e) => changePricing('INR', 'price', e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Original Price (₹)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.originalPrice}
          onChange={(e) => changePricing('INR', 'originalPrice', e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Discount (%)">
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          value={form.discount}
          onChange={(e) => changePricing('INR', 'discount', e.target.value)}
          className={inputCls}
          placeholder="e.g. 20"
        />
      </Field>
      <Field label="Return Window (Days)">
        <input type="number" min="0" value={form.returnWindowDays} onChange={(e) => setForm({ ...form, returnWindowDays: e.target.value })} className={inputCls} />
      </Field>
        </div>
      </SectionCard>

      <SectionCard title="Pricing & Stock (International - USD)" icon={FiGlobe} color="green">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Selling Price ($)">
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.usdPrice}
          onChange={(e) => changePricing('USD', 'usdPrice', e.target.value)}
          className={inputCls}
          placeholder="e.g. 12.99"
        />
      </Field>
      <Field label="Original Price ($)">
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.usdOriginalPrice}
          onChange={(e) => changePricing('USD', 'usdOriginalPrice', e.target.value)}
          className={inputCls}
          placeholder="e.g. 19.99"
        />
      </Field>
      <Field label="Discount (%)">
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          value={form.usdDiscount}
          onChange={(e) => changePricing('USD', 'usdDiscount', e.target.value)}
          className={inputCls}
          placeholder="e.g. 20"
        />
      </Field>
      <Field label="Return Window (Days)">
        <input type="number" min="0" value={form.internationalReturnWindowDays} onChange={(e) => setForm({ ...form, internationalReturnWindowDays: e.target.value })} className={inputCls} />
      </Field>
        </div>
      </SectionCard>
    </div>
  );
};

export const StockAndTaxSection = ({ form, setForm }) => (
  <SectionCard title="Inventory & Tax" icon={FiTag}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Stock *">
        <input type="number" min="0" required value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Bestseller Rank">
        <input type="number" min="1" value={form.bestsellerRank} onChange={(e) => setForm({ ...form, bestsellerRank: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Tax Rate (%)">
        <input type="number" min="0" max="100" step="0.01" value={form.taxRate} onChange={(e) => setForm({ ...form, taxRate: e.target.value })} className={inputCls} placeholder="e.g. 5" />
      </Field>
      <Field label="Tax Display Label">
        <input type="text" maxLength="120" value={form.taxLabel} onChange={(e) => setForm({ ...form, taxLabel: e.target.value })} className={inputCls} placeholder="e.g. Inclusive of GST" />
      </Field>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input type="checkbox" checked={form.taxInclusive} onChange={(e) => setForm({ ...form, taxInclusive: e.target.checked })} />
        Selling price includes tax
      </label>
    </div>
  </SectionCard>
);

export const PublishingSection = ({ form, setForm }) => (
  <SectionCard title="Publishing" icon={FiBookOpen} color="cyan">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Publisher">
        <input
          type="text"
          value={form.publisher}
          onChange={(e) => setForm({ ...form, publisher: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="ISBN-10">
        <input
          type="text"
          inputMode="text"
          maxLength={20}
          placeholder="e.g. 1612681131"
          title="1–10 letters or numbers; spaces and hyphens are ignored"
          value={form.isbn10}
          onChange={(e) => setForm({ ...form, isbn10: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="ISBN-13">
        <input
          type="text"
          inputMode="text"
          maxLength={20}
          placeholder="e.g. 9781612681139"
          title="1–13 letters or numbers; spaces and hyphens are ignored"
          value={form.isbn13}
          onChange={(e) => setForm({ ...form, isbn13: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Binding">
        <select
          value={form.binding}
          onChange={(e) => setForm({ ...form, binding: e.target.value })}
          className={inputCls}
        >
          <option value="Paperback">Paperback</option>
          <option value="Hardcover">Hardcover</option>
          <option value="Ebook">Ebook</option>
          <option value="Audiobook">Audiobook</option>
        </select>
      </Field>
      <Field label="Edition">
        <input
          type="text"
          placeholder="e.g. First Edition"
          value={form.edition || ''}
          onChange={(e) => setForm({ ...form, edition: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Reading age">
        <input
          type="text"
          placeholder="e.g. Customer suggested age: 12 years and up"
          maxLength={120}
          value={form.readingAge || ''}
          onChange={(e) => setForm({ ...form, readingAge: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Country of Origin">
        <input
          type="text"
          value={form.countryOfOrigin || ''}
          onChange={(e) => setForm({ ...form, countryOfOrigin: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Net Quantity">
        <input
          type="text"
          placeholder="e.g. 1 Count"
          value={form.netQuantity || ''}
          onChange={(e) => setForm({ ...form, netQuantity: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Language">
        <input
          type="text"
          value={form.language}
          onChange={(e) => setForm({ ...form, language: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Publisher Date">
        <input
          type="date"
          value={form.publisherDate}
          onChange={(e) => setForm({ ...form, publisherDate: e.target.value })}
          className={inputCls}
        />
      </Field>
    </div>
  </SectionCard>
);

export const DimensionsSection = ({ form, setForm }) => (
  <SectionCard title="Dimensions" icon={FiPackage} color="cyan">
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <Field label="Pages">
        <input
          type="number"
          value={form.pages}
          onChange={(e) => setForm({ ...form, pages: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Weight (g)">
        <input
          type="number"
          step="0.01"
          value={form.weight}
          onChange={(e) => setForm({ ...form, weight: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Height (cm)">
        <input
          type="number"
          step="0.001"
          value={form.height}
          onChange={(e) => setForm({ ...form, height: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Width (cm)">
        <input
          type="number"
          step="0.001"
          value={form.width}
          onChange={(e) => setForm({ ...form, width: e.target.value })}
          className={inputCls}
        />
      </Field>
      <Field label="Spine (cm)">
        <input
          type="number"
          step="0.001"
          value={form.spineWidth}
          onChange={(e) => setForm({ ...form, spineWidth: e.target.value })}
          className={inputCls}
        />
      </Field>
    </div>
  </SectionCard>
);

export const DescriptionSection = ({ form, setForm }) => (
  <SectionCard title="About the Book" icon={FiFileText} color="rose">
    <Field label="Description">
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={3}
        className={inputCls}
      />
    </Field>
    <div className="mt-3">
      <Field label="About the Author">
        <textarea
          value={form.authorBio}
          onChange={(e) => setForm({ ...form, authorBio: e.target.value })}
          rows={3}
          placeholder="Enter the author's biography"
          className={inputCls}
        />
      </Field>
    </div>
    <div className="mt-3">
      <Field label="Related Categories (comma separated)">
        <input
          type="text"
          placeholder="Self Help, Personal Development, Finance"
          value={form.relatedCategories}
          onChange={(e) => setForm({ ...form, relatedCategories: e.target.value })}
          className={inputCls}
        />
      </Field>
    </div>
  </SectionCard>
);

export const PreorderSection = ({ form, setForm }) => (
  <SectionCard title="Pre-order & Availability" icon={FiCalendar} color="violet">
    <div className="flex items-start gap-3">
      <input id="book-is-preorder" type="checkbox" checked={form.isPreorder} onChange={(e) => setForm({ ...form, isPreorder: e.target.checked })} className="mt-1 rounded" />
      <label htmlFor="book-is-preorder" className="cursor-pointer">
        <span className="block text-xs font-semibold text-violet-700">Enable pre-order</span>
        <span className="block text-[10px] text-slate-500">Requires a future release date and allocation stock greater than zero.</span>
      </label>
    </div>
    {form.isPreorder && (
      <div className="mt-3 grid w-full grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Pre-order Availability Date *">
          <input type="date" required value={form.preorderReleaseDate} onChange={(e) => setForm({ ...form, preorderReleaseDate: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Pre-order Customer Message">
          <textarea value={form.preorderMessage || ''} onChange={(e) => setForm({ ...form, preorderMessage: e.target.value })} maxLength={500} rows={3} placeholder="e.g. This book will ship on or after its release date." className={inputCls} />
          <span className="mt-1 block text-right text-[11px] text-violet-700">{(form.preorderMessage || '').length}/500</span>
        </Field>
      </div>
    )}
  </SectionCard>
);

export const ImageAndFlagsSection = ({ form, setForm, onImageChange, currentImage }) => (
  <SectionCard title="Cover Image" icon={FiImage} color="violet">
    <ImageUploadField label="Cover Image" currentImage={currentImage} onImageChange={onImageChange} compact />
    <div className="mt-3 flex flex-wrap items-center gap-4 text-slate-700">
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={form.isBestseller}
        onChange={(e) => setForm({ ...form, isBestseller: e.target.checked })}
        className="rounded"
      />
      Bestseller
    </label>
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={form.returnable}
        onChange={(e) => setForm({ ...form, returnable: e.target.checked })}
        className="rounded"
      />
      Returnable
    </label>
    </div>
  </SectionCard>
);
